import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { StickerTabs } from '../components/StickerTabs';
import { RichTextEditor } from './LazyEditors';
import { GET_BASE_URL_IMAGE } from '../constants/apiEndpoints';
import { insertCommentMention } from '../utils/commentMentionInsert';
import { insertStickerIntoRichTextEditor } from '../utils/richTextStickerInsert';
import {
  isCommentSubmitInFlight,
  waitForCommentSubmitLock,
} from '../utils/commentUtils';
import { useMentionSuggestions } from '../utils/useMentionSuggestions';


type CommenBoxPropsType = {
  chatId: any;
  onSubmit: any;
  register?: any;
  handleSubmit?: any;
  setValue?: any;
  stickerData: any;
};

type FormData = {
  chat_msg: string;
  chat_id: number;
};

export const CommentBox: React.FC<CommenBoxPropsType> = ({
  chatId,
  onSubmit,
  stickerData,
}) => {
  const { register, setValue, handleSubmit } = useForm<FormData>();
  const [showSticker, SetShowSticker] = useState<any | string>(false);
  const token = localStorage.getItem('token');

  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  
  const textRef = useRef(null);

  const {
    suggestions: filterUser,
    searchText,
    setSearchText,
    mentionSource,
    clearSuggestions,
    markMentionSelected,
  } = useMentionSuggestions();

  useEffect(() => {
    setValue('chat_msg', text);
    if (text === '' || text === '<p><br></p>') {
      clearSuggestions();
    }

    }, [setValue, text, clearSuggestions]);

  useEffect(() => {
    if (!isCommentSubmitInFlight(chatId)) {
      return;
    }

    let cancelled = false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    waitForCommentSubmitLock(chatId).finally(() => {
      if (!cancelled) {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [chatId]);

  
  const syncTextToForm = (nextText: string) => {
    setText(nextText);
    setValue('chat_msg', nextText, { shouldDirty: true });
  };

  const onClickSticker = (data: any) => {
    clearSuggestions();
    insertStickerIntoRichTextEditor(textRef, data, text, syncTextToForm);
  }

  let navigate = useNavigate();
  const openSticker = () => {
    if (token === null) {
      navigate('/disneyland/login');
    } else {
      SetShowSticker(!showSticker);
    }
  };

  const mentions = useMemo(
    () => ({
      allowedChars: /^[A-Za-z0-9_\-\sÅÄÖåäö]*$/,
      mentionDenotationChars: ['@'],
      source: mentionSource,
    }),
    [mentionSource]
  );

  const onChangeFilterUser = (item: any) => {
    const username = String(item?.value ?? '').replace(/^@/, '').trim();

    markMentionSelected(username);

    const nextText = insertCommentMention({
      editor: (textRef.current as any)?.getEditor?.(),
      currentText: text,
      searchText,
      username,
    });

    setText(nextText);
    setValue('chat_msg', nextText);
    setSearchText('');
    clearSuggestions();
  }

  const submitComment = async (data: FormData) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const didPost = await onSubmit(data);
      if (didPost) {
        setText('');
        clearSuggestions();
        SetShowSticker(false);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div >
      
      <form
        className='space-y-6'
        onSubmit={handleSubmit(submitComment)}
        method='POST'
      >
        <div className='com-box-main' style={{'position': 'relative'}}>
          {filterUser.length > 0 && (
          <div className="tagUserList mw-mention-picker" role="listbox" aria-label="Tag a MouseWait user">
          {
            filterUser.map((item, index) => {
              return (
                <div className="tagUserItem" key={item?.id ?? item?.value ?? index}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onChangeFilterUser(item)}
                    className="button"
                  >
                    <div>
                      <img
                        style={{ verticalAlign: 'middle' }}
                        src={
                          GET_BASE_URL_IMAGE +
                          '/disneyland/images/thumbs/' +
                          item['image']
                        }
                        className='com-imggg'
                        alt=""
                      />
                    </div>
                    
                    <div>
                      @{item['value']}
                    </div>
                  </button>
                </div>
              )
            })
          }
          </div>
          )}

          <div className='com-box d-flex'>
            <RichTextEditor
              id='rte'
              placeholder='Add your magic...'
              mentions={mentions}
              value={text}
              onChange={setText}
              controls={[[]]}
              ref={textRef}
            />

            <input
              type='hidden'
              readOnly={true}
              {...register('chat_id')}
              defaultValue={chatId}
            />

            <div className='icon-ic d-flex'>
              <div
                className='icon-ic0'
                role='button'
                aria-label='Post comment'
                aria-disabled={isSubmitting}
                aria-busy={isSubmitting}
                onClick={isSubmitting ? undefined : handleSubmit(submitComment)}
              ></div>
              <div className='icon-ic1' onClick={openSticker}></div>
            </div>
          </div>
        </div>
      </form>
      {showSticker === true && (
        <div className='mw-sticker-popover-wrap'>
          <button
            type='button'
            className='mw-sticker-popover-close'
            aria-label='Close sticker picker'
            onClick={() => SetShowSticker(false)}
          >
            ×
          </button>
          <StickerTabs
            tabData={stickerData}
            onClickSticker={onClickSticker}
            onStickerInserted={() => SetShowSticker(false)}
          />
        </div>
      )}
    </div>
  );
};
